// GreTunnelPanel.jsx - tab "GRE Tunnel" di AWS Inventory: status egress EC2 Jakarta -> PIL_NAT.
// Data dari backend/netTunnel.js (cron gre_pil_nat). Teks literal Inggris (bukan i18n).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, RefreshCw, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { api } from '../context/AuthContext';

const STATUS = {
  up:        { label: 'Up',        cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: '#10b981' },
  degraded:  { label: 'Degraded',  cls: 'bg-amber-50 text-amber-700 ring-amber-200',       dot: '#f59e0b' },
  down:      { label: 'Down',      cls: 'bg-rose-50 text-rose-700 ring-rose-200',          dot: '#e11d48' },
  host_down: { label: 'Host down', cls: 'bg-rose-50 text-rose-700 ring-rose-200',          dot: '#e11d48' },
  error:     { label: 'Error',     cls: 'bg-slate-100 text-slate-700 ring-slate-200',      dot: '#64748b' },
  inactive: { label: 'Not in use', cls: 'bg-slate-50 text-slate-500 ring-slate-200', dot: '#94a3b8' },
};
const RANGES = [[6, '6h'], [24, '24h'], [168, '7d'], [720, '30d']];

// checked_at disimpan UTC ('YYYY-MM-DD HH:MM:SS') -> tampilkan waktu lokal.
const toDate = (s) => (s ? new Date(s.replace(' ', 'T') + 'Z') : null);
const fmt = (s) => { const d = toDate(s); return d ? d.toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '-'; };
const dur = (a, b) => {
  const ms = (b ? toDate(b) : new Date()) - toDate(a);
  const m = Math.max(1, Math.round(ms / 60000));
  return m < 60 ? `${m} min` : m < 1440 ? `${(m / 60).toFixed(1)} h` : `${(m / 1440).toFixed(1)} d`;
};

function Badge({ status }) {
  const s = STATUS[status] || STATUS.error;
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${s.cls}`}>
    <span className="w-2 h-2 rounded-full" style={{ background: s.dot }} />{s.label}</span>;
}

function Tile({ label, value, sub }) {
  return <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="text-xs font-medium text-slate-500">{label}</div>
    <div className="mt-1 text-xl font-semibold text-slate-800 tabular-nums">{value}</div>
    {sub && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
  </div>;
}

function RttChart({ rows }) {
  const W = 800, H = 160, P = 28;
  const pts = rows.filter((r) => r.rtt_ms != null);
  if (rows.length < 2) return <div className="text-sm text-slate-400 py-10 text-center">Not enough data yet.</div>;
  const t0 = toDate(rows[0].checked_at).getTime(), t1 = toDate(rows[rows.length - 1].checked_at).getTime() || t0 + 1;
  const max = Math.max(20, ...pts.map((r) => r.rtt_ms)) * 1.15;
  const x = (r) => P + ((toDate(r.checked_at).getTime() - t0) / Math.max(1, t1 - t0)) * (W - P * 2);
  const y = (v) => H - P - (v / max) * (H - P * 2);
  const line = pts.map((r, i) => `${i ? 'L' : 'M'}${x(r).toFixed(1)},${y(r.rtt_ms).toFixed(1)}`).join('');
  const bad = rows.filter((r) => r.status !== 'up');
  return <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40">
    {[0, 0.5, 1].map((f) => <g key={f}>
      <line x1={P} x2={W - P} y1={y(max * f)} y2={y(max * f)} stroke="#e2e8f0" />
      <text x={2} y={y(max * f) + 4} fontSize="10" fill="#94a3b8">{Math.round(max * f)}</text></g>)}
    {bad.map((r, i) => <rect key={i} x={x(r) - 2} y={P} width={4} height={H - P * 2}
      fill={(STATUS[r.status] || STATUS.error).dot} opacity="0.25"><title>{fmt(r.checked_at)} · {r.status}</title></rect>)}
    <path d={line} fill="none" stroke="#2563eb" strokeWidth="1.8" />
    <text x={W - P} y={H - 6} fontSize="10" fill="#94a3b8" textAnchor="end">{fmt(rows[rows.length - 1].checked_at)}</text>
    <text x={P} y={H - 6} fontSize="10" fill="#94a3b8">{fmt(rows[0].checked_at)}</text>
  </svg>;
}

export default function GreTunnelPanel() {
  const [hours, setHours] = useState(24);
  const [tunnel, setTunnel] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setErr(null);
    api.get('/api/net/tunnel/health', { params: tunnel ? { hours, tunnel } : { hours } })
      .then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.error || e.message));
  }, [hours, tunnel]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const checkNow = () => {
    setBusy(true);
    api.post('/api/net/tunnel/check').then(load).catch((e) => setErr(e.response?.data?.error || e.message))
      .finally(() => setBusy(false));
  };

  const latest = data?.latest;
  const cfg = data?.config || {};
  const isVpn = data?.kind === 'vpn';
  const cur = (data?.tunnels || []).find((t) => t.name === data?.tunnel);
  const avgRtt = useMemo(() => {
    const v = (data?.rows || []).map((r) => r.rtt_ms).filter((n) => n != null);
    return v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1) : '-';
  }, [data]);

  return <div className="space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Activity className="text-blue-600" style={{ width: 20, height: 20 }} />
        <div>
          <div className="text-lg font-semibold text-slate-800">{isVpn ? data.tunnel : 'GRE Tunnel · Jakarta egress via PIL_NAT'}</div>
          <div className="text-xs text-slate-500">{isVpn
            ? `AWS Site-to-Site VPN ${latest?.egress_ip || ''}${cur?.probe ? ' · probe ' + cur.probe : ' · AWS tunnel telemetry only'}`
            : `${cfg.instanceId} (${cfg.hostIp}) → gre1 → ${cfg.peerIp} · expected egress ${cfg.expectIp}`}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex rounded-xl bg-slate-100 p-1">
          {RANGES.map(([h, l]) => <button key={h} onClick={() => setHours(h)}
            className={`px-3 py-1 text-xs font-medium rounded-lg ${hours === h ? 'bg-white shadow ring-1 ring-slate-200 text-slate-800' : 'text-slate-500'}`}>{l}</button>)}
        </div>
        <button onClick={checkNow} disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-blue-600 text-white text-xs font-medium disabled:opacity-60">
          <RefreshCw style={{ width: 14, height: 14 }} className={busy ? 'animate-spin' : ''} /> {busy ? 'Checking…' : 'Check now'}
        </button>
      </div>
    </div>

    {data?.tunnels && <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
      {data.tunnels.map((t) => <button key={t.name} onClick={() => setTunnel(t.name)}
        className={`text-left rounded-2xl border p-3 transition ${t.name === data.tunnel ? 'border-blue-500 ring-2 ring-blue-100 bg-white' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
        <div className="text-xs font-semibold text-slate-700 truncate">{t.kind === 'gre' ? 'GRE PGOC → PIL_NAT' : t.name.replace(/^VPN /, 'VPN ')}</div>
        <div className="mt-1.5 flex items-center justify-between gap-2">
          {t.latest ? <Badge status={t.latest.status} /> : <span className="text-xs text-slate-400">no data</span>}
          <span className="text-xs text-slate-500 tabular-nums">{t.latest?.rtt_ms != null ? `${t.latest.rtt_ms} ms` : t.latest?.ssm_status || ''}</span>
        </div>
      </button>)}
    </div>}

    {err && <div className="flex items-center gap-2 rounded-xl bg-rose-50 text-rose-700 text-sm px-3 py-2">
      <AlertTriangle style={{ width: 16, height: 16 }} /> {err}</div>}

    {!data ? <div className="text-sm text-slate-400">Loading…</div> : !latest
      ? <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-sm text-slate-500">
          No check recorded yet. Click <b>Check now</b>, or wait for cron <code>gre_pil_nat</code> (every 5 minutes).</div>
      : <>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="text-xs font-medium text-slate-500">Status</div>
            <div className="mt-2"><Badge status={latest.status} /></div>
            <div className="mt-1 text-xs text-slate-400">{fmt(latest.checked_at)}</div>
          </div>
          <Tile label={isVpn ? 'Probe RTT (TCP)' : 'RTT (last)'} value={latest.rtt_ms != null ? `${latest.rtt_ms} ms` : '-'} sub={`avg ${avgRtt} ms`} />
          <Tile label={isVpn ? 'Probe loss' : 'Packet loss'} value={latest.tunnel_loss != null ? `${latest.tunnel_loss}%` : '-'} sub={isVpn ? (cur?.probe || 'no probe') : 'to tunnel peer'} />
          {isVpn
            ? <Tile label="AWS tunnels" value={latest.ssm_status || '-'} sub="VgwTelemetry" />
            : <Tile label="Egress IP" value={latest.egress_ip || '-'}
                sub={latest.egress_ip === cfg.expectIp ? 'matches PIL_NAT' : 'unexpected'} />}
          <Tile label={`Uptime (${RANGES.find((r) => r[0] === hours)?.[1]})`}
            value={data.uptimePct != null ? `${data.uptimePct}%` : '-'} sub={`${data.rows.length} checks`} />
        </div>
        {latest.detail && <div className="text-xs text-slate-500">Last detail: {latest.detail}</div>}

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-700 mb-2">{isVpn ? 'Probe RTT to site host (ms)' : 'RTT to tunnel peer (ms)'}</div>
          <RttChart rows={data.rows} />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-700 mb-2">Outage history (90 days)</div>
          {data.outages.length === 0
            ? <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 style={{ width: 16, height: 16 }} /> No outages recorded.</div>
            : <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-500 border-b">
                  <th className="py-1.5">Start</th><th>End</th><th>Duration</th><th>Status</th><th>Detail</th></tr></thead>
                <tbody>{data.outages.map((o, i) => <tr key={i} className="border-b last:border-0">
                  <td className="py-1.5 tabular-nums">{fmt(o.start)}</td>
                  <td className="tabular-nums">{o.end ? fmt(o.end) : <span className="inline-flex items-center gap-1 text-rose-600"><XCircle style={{ width: 14, height: 14 }} />ongoing</span>}</td>
                  <td className="tabular-nums">{dur(o.start, o.end)}</td>
                  <td><Badge status={o.status} /></td>
                  <td className="text-xs text-slate-500">{o.detail || '-'}</td></tr>)}</tbody>
              </table>}
        </div>
      </>}
  </div>;
}
